import type { PostgrestError } from "@supabase/supabase-js";
import { getClientEnv, isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  createPqrPagination,
  isKnownPqrStatus,
  mapPqrHistoryItem,
  mapPqrRequestDetails,
  mapPqrThreadMessage,
} from "@/features/pqr/pqrMappers";
import {
  type PqrFunctionErrorCode,
  type PqrHistoryPage,
  type PqrRequestDetails,
  type PqrThreadMessage,
  type PqrSubmissionDraft,
  type PqrSubmissionResult,
  pqrPageSize,
} from "@/features/pqr/pqr.types";

type PqrFunctionErrorBody = {
  code?: string;
  fileName?: string;
  message?: string;
};

type StagePqrAttachmentsResponse = {
  attachmentPaths?: string[];
};

type SubmitPqrResponse = {
  createdAt?: string;
  status?: string;
  ticketRef?: string;
};

export class PqrClientError extends Error {
  constructor(
    public readonly code: PqrFunctionErrorCode | "generic",
    message?: string,
    public readonly cause?: unknown,
    public readonly fileName?: string,
  ) {
    super(message ?? code);
    this.name = "PqrClientError";
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new PqrClientError("generic", "Supabase environment variables are not configured.");
  }
}

function normalizePqrFunctionError(error: unknown) {
  if (!error || typeof error !== "object") {
    return {
      code: "PQR_INSERT_FAILED",
      message: "PQR_INSERT_FAILED",
    } satisfies PqrFunctionErrorBody;
  }

  if ("context" in error && typeof error.context === "object" && error.context) {
    const context = error.context as { json?: PqrFunctionErrorBody };
    if (context.json) {
      return context.json;
    }
  }

  return {
    code: "message" in error ? String(error.message) : "PQR_INSERT_FAILED",
    message: "message" in error ? String(error.message) : "PQR_INSERT_FAILED",
  } satisfies PqrFunctionErrorBody;
}

function mapPqrFunctionErrorCode(code: string | null | undefined) {
  switch (code) {
    case "PQR_UNAUTHORIZED":
    case "PQR_INVALID_TICKET_REF":
    case "PQR_TOO_MANY_ATTACHMENTS":
    case "PQR_INVALID_ATTACHMENT_TYPE":
    case "PQR_ATTACHMENT_TOO_LARGE":
    case "PQR_INVALID_CATEGORY":
    case "PQR_INVALID_NAME":
    case "PQR_INVALID_EMAIL":
    case "PQR_INVALID_SUBJECT":
    case "PQR_INVALID_MESSAGE":
    case "PQR_INVALID_REPLY_MESSAGE":
    case "PQR_REPLY_UNAVAILABLE":
    case "PQR_STORAGE_UPLOAD_FAILED":
    case "PQR_INSERT_FAILED":
      return code;
    default:
      return "generic";
  }
}

function mapPostgrestInsertError(error: PostgrestError) {
  return new PqrClientError("PQR_INSERT_FAILED", error.message, error);
}

async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  return session?.access_token ?? null;
}

async function postMultipartToPqrFunction<T>(
  functionName: string,
  formData: FormData,
  requiresAuth: boolean,
) {
  ensureConfigured();

  const headers = new Headers();
  if (requiresAuth) {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new PqrClientError("PQR_UNAUTHORIZED");
    }

    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const response = await fetch(`${getClientEnv().supabaseUrl}/functions/v1/${functionName}`, {
    body: formData,
    headers,
    method: "POST",
  });

  const body = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    const errorBody = body as PqrFunctionErrorBody;
    throw new PqrClientError(
      mapPqrFunctionErrorCode(errorBody.code),
      errorBody.message,
      errorBody,
      errorBody.fileName,
    );
  }

  return body as T;
}

export async function reservePqrTicketRef() {
  ensureConfigured();

  const { data, error } = await supabase.rpc("generate_ticket_ref");
  if (error || typeof data !== "string") {
    throw new PqrClientError("PQR_INVALID_TICKET_REF", error?.message, error);
  }

  return data;
}

export async function stagePqrAttachments(ticketRef: string, files: File[]) {
  if (files.length === 0) {
    return [];
  }

  const formData = new FormData();
  formData.set("ticketRef", ticketRef);
  files.forEach((file) => {
    formData.append("files[]", file);
  });

  const result = await postMultipartToPqrFunction<StagePqrAttachmentsResponse>(
    "stage-pqr-attachments",
    formData,
    true,
  );

  return result.attachmentPaths ?? [];
}

export async function submitGuestPqr(
  values: PqrSubmissionDraft,
  files: File[],
): Promise<PqrSubmissionResult> {
  const formData = new FormData();
  formData.set("payload", JSON.stringify(values));
  files.forEach((file) => {
    formData.append("files[]", file);
  });

  const result = await postMultipartToPqrFunction<SubmitPqrResponse>("submit-pqr", formData, false);

  return {
    createdAt: result.createdAt ?? new Date().toISOString(),
    status: result.status && isKnownPqrStatus(result.status) ? result.status : "open",
    ticketRef: result.ticketRef ?? "",
  };
}

export async function submitAuthenticatedPqr(
  values: PqrSubmissionDraft,
  files: File[],
): Promise<PqrSubmissionResult> {
  ensureConfigured();

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError || !user) {
    throw new PqrClientError("PQR_UNAUTHORIZED", authError?.message, authError);
  }

  const ticketRef = await reservePqrTicketRef();
  const attachmentPaths = await stagePqrAttachments(ticketRef, files);

  const insertResult = await supabase
    .from("pqr_submissions")
    .insert({
      attachment_paths: attachmentPaths,
      category: values.category,
      email: values.email,
      message: values.message,
      name: values.name,
      status: "open",
      subject: values.subject,
      ticket_ref: ticketRef,
      user_id: user.id,
    })
    .select("created_at, status, ticket_ref")
    .single();

  if (insertResult.error || !insertResult.data) {
    throw mapPostgrestInsertError(insertResult.error ?? {
      code: "PGRST000",
      details: "",
      hint: "",
      message: "PQR insert failed.",
      name: "PostgrestError",
    } as PostgrestError);
  }

  return {
    createdAt: insertResult.data.created_at,
    status: isKnownPqrStatus(insertResult.data.status) ? insertResult.data.status : "open",
    ticketRef: insertResult.data.ticket_ref,
  };
}

export async function loadMyPqrHistory(page = 1): Promise<PqrHistoryPage> {
  ensureConfigured();

  const requestedPage = Math.max(page, 1);
  const rangeFrom = (requestedPage - 1) * pqrPageSize;
  const rangeTo = rangeFrom + pqrPageSize - 1;

  const result = await supabase
    .from("pqr_submissions")
    .select("id, ticket_ref, category, subject, status, created_at, attachment_paths", {
      count: "exact",
    })
    .order("created_at", { ascending: false })
    .range(rangeFrom, rangeTo);

  if (result.error) {
    throw new PqrClientError("generic", result.error.message, result.error);
  }

  const pagination = createPqrPagination({
    page,
    totalCount: result.count ?? 0,
  });

  return {
    items: (result.data ?? []).map((row) => mapPqrHistoryItem(row)),
    page: pagination.page,
    pageCount: pagination.pageCount,
    totalCount: pagination.totalCount,
  };
}

export async function loadMyPqrRequestDetails(requestId: string): Promise<PqrRequestDetails> {
  ensureConfigured();

  const result = await supabase
    .from("pqr_submissions")
    .select("*")
    .eq("id", requestId)
    .single();

  if (result.error || !result.data) {
    throw new PqrClientError("generic", result.error?.message, result.error);
  }

  return mapPqrRequestDetails(result.data);
}

export async function loadPqrReplyThread(requestId: string): Promise<PqrThreadMessage[]> {
  ensureConfigured();

  const result = await supabase
    .from("pqr_thread_messages")
    .select("*")
    .eq("submission_id", requestId)
    .order("created_at", { ascending: true });

  if (result.error) {
    throw new PqrClientError("generic", result.error.message, result.error);
  }

  return (result.data ?? []).map((row) => mapPqrThreadMessage(row));
}

export function toPqrClientError(error: unknown) {
  if (error instanceof PqrClientError) {
    return error;
  }

  const normalized = normalizePqrFunctionError(error);
  return new PqrClientError(
    mapPqrFunctionErrorCode(normalized.code),
    normalized.message,
    error,
    normalized.fileName,
  );
}
